import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { supabase } from "../lib/supabase";
import LoadingSpinner from "../../packages/ui/src/components/LoadingSpinner";
import SEO from "./SEO";

export default function AdminCourseTags() {
  const [tags, setTags] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTag, setEditingTag] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
  });

  useEffect(() => {
    fetchTags();
  }, []);

  async function fetchTags() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("course_tags")
        .select("*")
        .order("name");

      if (error) throw error;
      setTags(data || []);
    } catch (err) {
      console.error("Error fetching tags:", err);
      setError("Failed to load tags");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      if (editingTag) {
        const { error } = await supabase
          .from("course_tags")
          .update(formData)
          .eq("id", editingTag.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from("course_tags").insert([formData]);

        if (error) throw error;
      }

      setFormData({ name: "", slug: "" });
      setEditingTag(null);
      fetchTags();
    } catch (err) {
      console.error("Error saving tag:", err);
      alert("Failed to save tag");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Are you sure you want to delete this tag?")) return;

    try {
      const { error } = await supabase
        .from("course_tags")
        .delete()
        .eq("id", id);

      if (error) throw error;
      fetchTags();
    } catch (err) {
      console.error("Error deleting tag:", err);
      alert("Failed to delete tag");
    }
  }

  function handleEdit(tag: any) {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      slug: tag.slug,
    });
  }

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="One80Labs - Manage Course Tags"
        description="Admin interface for managing course tags in the One80Labs content management system."
        noindex={true}
      />
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Course Tags</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingTag ? "Edit Tag" : "Add New Tag"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="slug"
                  className="block text-sm font-medium text-gray-700"
                >
                  Slug
                </label>
                <input
                  type="text"
                  id="slug"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData({ ...formData, slug: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  required
                />
              </div>

              <div className="flex justify-end space-x-3">
                {editingTag && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTag(null);
                      setFormData({ name: "", slug: "" });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  {editingTag ? "Update Tag" : "Add Tag"}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tag List</h3>
            <div className="space-y-4">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-md"
                >
                  <span className="text-gray-900">{tag.name}</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEdit(tag)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(tag.id)}
                      className="text-red-400 hover:text-red-500"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
